/* eslint-disable @typescript-eslint/no-explicit-any */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { FC, Ref, useRef, useState, useEffect } from "react";
import cx from "classnames";
import Select, { MultiValue } from "react-select";
import { useFormatMsg } from "src/hooks/useFormatMsg";
import {
  AcceptableSelectOnChange,
  Option,
  OptionLabel,
  SelectedOptionValue,
} from "./types";
import { flowioMultiSelectCss } from "./styles";

const FlowioMultiSelect: FC<Props> = (props) => {
  const {
    options,
    value: valueIn,
    placeholder,
    onChange,
    onBlur,
    disabled = false,
    className,
    name,
    required = false,
  } = props;
  const formatMsg = useFormatMsg();
  const [width, setWidth] = useState<number>(0);
  const ref = useRef<HTMLDivElement | null>(null);
  const hasError = required && !valueIn;

  const fallbackValue = options[0] ?? "";

  const isControlled = valueIn !== undefined;
  const value = (isControlled ? valueIn ?? fallbackValue : []) as string[];

  const handleChange = (
    e: MultiValue<{ label: OptionLabel; value: string }>
  ): void => {
    const res = e.map((item: any) => item.value) as string[];
    if (!onChange) return;
    onChange(res);
  };

  useEffect(() => {
    if (ref && ref.current) {
      setWidth(ref.current.offsetWidth);
    }
  }, []);

  return (
    <div ref={ref}>
      <Select
        name={name}
        value={
          options
            ?.filter((data) => value.includes(data.name as string))
            .map((option) => ({
              label: option.name,
              value: option.value,
            })) ?? []
        }
        data-cy="input flowio-select-input"
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={placeholder ?? ""}
        required={required}
        isDisabled={disabled}
        css={flowioMultiSelectCss(hasError, width)}
        className={cx("basic-select", className)}
        options={options.map((option) => ({
          label: option.name,
          value: option.value,
        }))}
        menuPortalTarget={document.body}
        noOptionsMessage={() => formatMsg("common.no_options")}
        closeMenuOnSelect={false}
        isSearchable
        isMulti
        styles={{
          control: (styles) => ({
            ...styles,
            width: `${width}px`,
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            borderRadius: "25px",
          }),
          menuPortal: (portal) => ({
            ...portal,
            zIndex: "9999",
          }),
        }}
      />
    </div>
  );
};

// Value related

export type UncontrolledOnlyProps = {
  value?: undefined;
  defaultValue: SelectedOptionValue;
  inputRef: Ref<HTMLInputElement>;
};

export type ControlledOnlyProps = {
  value: [];
  defaultValue?: undefined;
  inputRef?: undefined;
};

export type RefControlledOnlyProps = {
  value?: undefined;
  defaultValue?: undefined | string;
  inputRef?: Ref<HTMLInputElement>;
};

export type ValueRelatedProps =
  | ControlledOnlyProps
  | UncontrolledOnlyProps
  | RefControlledOnlyProps;

// Props

type Props = ValueRelatedProps & {
  options: Option[];
  onChange?: AcceptableSelectOnChange;
  onBlur?(): void;
  disabled?: boolean;
  className?: string;
  name?: string | undefined;
  placeholder?: string;
  required?: boolean;
};

export default FlowioMultiSelect;
export type FlowioMultiSelectProps = Props;
